<template>
  <RevDrawer
    :name="MODAL_NAMES.ABOUT_BATTERY_UPDATED"
    :title="i18n(translations.aboutBatteryHealth)"
  >
    <template #body>
      <div class="mb-24 h-[218px] overflow-hidden rounded-md">
        <RevIllustration
          alt=""
          class="h-full w-full object-cover"
          :height="218"
          role="presentation"
          :src="illustration"
          :width="327"
        />
      </div>

      <h3 class="heading-2 mb-16">
        {{ i18n(translations.bodyHeading) }}
      </h3>

      <p class="body-2 text-static-default-mid mb-24">
        {{ i18n(translations.description) }}
      </p>

      <div class="grid auto-rows-fr gap-12 mb-24">
        <div
          v-for="tier in batteryTiers"
          :key="tier.title.id"
          class="rounded-lg border border-static-default-low px-20 py-16 flex flex-col justify-center gap-8"
        >
          <span class="body-1-bold">{{ i18n(tier.title) }}</span>
          <span class="body-2 text-static-default-mid">{{
            i18n(tier.description)
          }}</span>
        </div>
      </div>

      <RevInfoBlock
        v-if="isSmartphoneCategory"
        class="mb-24"
        :icon="IconInfo"
        :title="i18n(translations.infoTitle)"
        variant="default"
      >
        <p>{{ i18n(translations.infoContent) }}</p>
        <RevLink v-if="link" target="_blank" :to="link">{{
          i18n(translations.learnMore)
        }}</RevLink>
      </RevInfoBlock>

      <BatteryFaq :has-external-borders="false" />
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { useRouteLocationWithLocale } from '#imports'
import { computed } from 'vue'

import type { LinkInternal } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevDrawer } from '@ds/components/Drawer'
import { RevIllustration } from '@ds/components/Illustration'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { IconInfo } from '@ds/icons/IconInfo'

import { LINKS } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/NewBatteryModal/NewBatteryModal.constants'
import BatteryFaq from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/NewBatteryModal/components/BatteryFaq/BatteryFaq.vue'

import { MODAL_NAMES } from '../../Product-JP.constants'

import translations from './AboutBatteryModalUpdated.translations'

const { illustration, isSmartphoneCategory } = defineProps<{
  illustration: string
  isSmartphoneCategory: boolean
}>()

const i18n = useI18n()

const {
  market: { countryCode },
} = useMarketplace()

const resolveLocalizedRoute = useRouteLocationWithLocale()

const batteryTiers = computed(() => [
  {
    title: translations.batteryTier1Title,
    description: translations.batteryTier1Description,
  },
  {
    title: translations.batteryTier2Title,
    description: translations.batteryTier2Description,
  },
  {
    title: translations.batteryTier3Title,
    description: translations.batteryTier3Description,
  },
])

const link = computed(() => {
  return LINKS[countryCode]
    ? resolveLocalizedRoute(LINKS[countryCode] as LinkInternal)
    : null
})
</script>
